import React from 'react';
import { ReactComponent as CodenceLogo } from './logos/abstract-c.svg';
import './App.css';

const App = () => {
  return (
    <div className="app-container">
      <div className="crt-overlay"></div>
      <nav className="nav-bar">
        <div className="brand-container">
          <CodenceLogo className="logo-icon" />
          <span className="brand-name mono">CyVec</span>
        </div>
        <ul className="nav-links">
          <li><a href="#hero">HOME</a></li>
          <li><a href="#summary">TEAM</a></li>
          <li><a href="#security-pillars">SECURITY</a></li>
          <li><a href="#operational-pillars">OPERATIONS</a></li>
          <li><a href="#services">SERVICES</a></li>
          <li><a href="#projects">PROJECTS</a></li>
          <li><a href="#contact">CONTACT</a></li>
        </ul>
      </nav>

      <section id="hero" className="hero-section">
        <div className="hero-content">
          <h1 className="glitch-text" data-text="ENGINEERING EXCELLENCE">ENGINEERING EXCELLENCE</h1>
          <p className="subtitle mono">Highly Scalable & Stable Distributed Systems</p>
          <div className="team-intro">
            <div className="member">
              <h3>George Zhou</h3>
              <p className="role">Software Engineer</p>
            </div>
            <div className="divider">|</div>
            <div className="member">
              <h3>Jun Chai</h3>
              <p className="role">Technical BA/PM</p>
            </div>
          </div>
        </div>
      </section>

      <section id="summary" className="summary-section">
        <h2 className="section-title">Professional Summary</h2>
        <div className="summary-grid">
          <div className="summary-card highlight">
            <h3>George Zhou | Security-First Engineer</h3>
            <p className="mono-sub">Big Tech | Cybersecurity | AppSec Lead</p>
            <p>
              Specialist in building secure, high-throughput distributed systems. Beyond core 
              infrastructure, I bridge the gap between engineering and data science, leading 
              RAG-based LLM initiatives and authoring validated science proposals. Expert in 
              implementing Defense-in-Depth strategies and the <b>STRIDE</b> framework to protect 
              mission-critical data.
            </p>
          </div>
         <div className="summary-card">
            <h3>Jun Chai | Technical BA/PM</h3>
            <p className="mono-sub">Streamline | Modernize | Risk Management</p>
            <p>
              Strategic professional specializing in translating complex business 
              requirements into streamlined, user-focused technical systems.

              Focusing on bridging the gap between stakeholders and development teams 
              to modernize legacy processes and enhance operational efficiency.
            </p>
          </div>
        </div>
      </section>

      <section id="security-pillars" className="pillars-section">
        <h2 className="section-title">Security Architecture</h2>
        <div className="pillars-grid">
          <div className="pillar-item">
            <h4 className="mono">Identity Propagation</h4>
            <p>We create a secure digital passport for every team member. This ensures that every time someone logs in or a system communicates, we know exactly who they are—keeping your internal tools locked down and verified without slowing down your workflow.</p>
          </div>
          <div className="pillar-item">
            <h4 className="mono">Custom Authorization</h4>
            <p>We make sure the right people have the right access, and nothing more. By building smart rules directly into our security, we automatically protect sensitive data and ensure that every employee has exactly what they need to do their job—safely.</p>
          </div>
          <div className="pillar-item">
            <h4 className="mono">Network Zero Trust</h4>
            <p>We don't just rely on one lock; we build a vault. By using multiple layers of security, we ensure that even if one barrier is crossed, your most important assets remain invisible and inaccessible to anyone who shouldn't be there.</p>
          </div>
          <div className="pillar-item">
            <h4 className="mono">Threat Modeling</h4>
            <p>We stop security problems before they even start. By training teams to spot risks during the design phase, we build safety into the foundation of every project—saving you the time, cost, and headache of fixing a breach after the fact.</p>
          </div>
        </div>
      </section>

      <section id="operational-pillars" className="pillars-section">
        <h2 className="section-title">Operational Excellence</h2>
        <div className="pillars-grid">
          <div className="pillar-item">
            <h4 className="mono">Infrastructure as Code (CDK)</h4>
            <p>We eliminate manual configuration and human error by treating your hardware like software. Using AWS CDK, we build automated, repeatable environments that slash setup time and ensure your infrastructure scales perfectly with your business growth.</p>
          </div>
          <div className="pillar-item">
            <h4 className="mono">Containerization (Docker)</h4>
            <p>We maximize efficiency and portabilty by packaging applications into secure containers. This ensures "environmental parity"—meaning your software works identically on a developer's laptop and in production—drastically reducing troubleshooting costs and deployment friction.</p>
          </div>
          <div className="pillar-item">
            <h4 className="mono">CI/CD Orchestration (Jenkins)</h4>
            <p>We accelerate your time-to-market while reducing risk. By building automated "quality gates" into your delivery pipeline, we catch bugs and security flaws early, allowing for rapid, low-risk software releases that keep your customers happy and your systems stable.</p>
          </div>
          <div className="pillar-item">
            <h4 className="mono">Multi-Cloud & Monitoring</h4>
            <p>We protect your uptime and your bottom line. By managing deployments across AWS and Azure with proactive alerting, we identify and solve performance bottlenecks before they ever impact your users, ensuring 24/7 reliability for high-traffic services.</p>
          </div>
        </div>
      </section>

      <section id="services" className="pillars-section">
        <h2 className="section-title">Our Services</h2>
        <div className="pillars-grid">
          <div className="pillar-item">
            <h4 className="mono">System Architecture</h4>
            <p>Designing highly scalable, secure, and resilient distributed systems from concept to deployment. Specializing in microservices, event-driven architectures, and domain-driven design principles.</p>
          </div>
          <div className="pillar-item">
            <h4 className="mono">Cloud Architecture</h4>
            <p>Expertise in architecting cloud-native solutions on AWS and Azure. Focusing on cost optimization, performance tuning, infrastructure-as-code (CDK), and leveraging managed services for maximum efficiency.</p>
          </div>
          <div className="pillar-item">
            <h4 className="mono">Web Development</h4>
            <p>Crafting modern, responsive web applications with exceptional user experiences. Specializing in React.js, TypeScript, and robust backend integrations for dynamic and interactive platforms.</p>
          </div>
        </div>
      </section>

      <section id="projects" className="projects-section">
        <h2 className="section-title">Core Systems</h2>
        <div className="projects-list">
          <ProjectCard 
            title="Enterprise RAG Chatbot Orchestrator" 
            tags={['RAG', 'LLM', 'OpenSearch Serverless', 'Titan V2']}
            description="Lead engineering-to-science initiative to design and deploy a highly accurate, context-aware chatbot."
            deepDive="Authored a comprehensive science proposal approved by research teams to evaluate 5 LLM candidates. Established an experimentation framework measuring Helpfulness, Correctness, and Faithfulness, ensuring sub-30s latency. Architected the production stack using OpenSearch Serverless with Titan V2 embeddings and implemented a daily batch data refresh pipeline to maintain RAG context integrity."
          />
          <ProjectCard 
            title="Distributed Lambda Cache" 
            tags={['AWS Lambda', 'Python', 'Redis', 'API Gateway']}
            description="A generic caching solution used across multiple internal engineering teams to optimize high-traffic API responses."
            deepDive="Implemented a generic Lambda cache layer that reduced median response times for key APIs from 640ms to 462ms (31.7% decrease). The system was designed for horizontal scalability and consistent performance under peak loads."
          />
          <ProjectCard 
            title="Scalable VM Automation Engine" 
            tags={['.NET', 'Node.js', 'Nginx', 'Azure']}
            description="Scalable microservices architecture designed to automate the lifecycle of tens of thousands of VMs monthly."
            deepDive="Built a robust test infrastructure system that automated the provisioning and destruction of VM clusters. Optimized throughput by implementing custom Nginx caching and Redis backends, significantly reducing time-to-test for critical product releases."
          />
          <ProjectCard 
            title="Secure LLM File-Upload Pipeline" 
            tags={['Security', 'Python', 'LLM', 'Docker']}
            description="End-to-end secure ingestion pipeline for contextual LLM conversations."
            deepDive="Designed and implemented a secure pipeline for file uploads at a major tech firm, enabling contextual ingestion for LLM applications. Focused on threat modeling and container security to ensure data integrity and compliance with rigorous internal security standards."
          />
          <ProjectCard 
            title="Real-time Payment Workflow Automation" 
            tags={['Java', 'Stripe API', 'ASP.NET']}
            description="Automation of complex financial workflows and payment processing."
            deepDive="Redesigned payment processing systems to integrate Stripe API for automated workflows. Additionally built custom management systems to automate pfSense router updates, improving overall network stability and infrastructure efficiency."
          />
          <ProjectCard 
            title="RMA System Modernization" 
            tags={['Workflow Optimization', 'Stakeholder Management', 'UX Design', 'SQL']}
            description="Modernization of a mission-critical legacy system to improve data accuracy and user adoption."
            deepDive="Led the end-to-end transformation of a legacy RMA system. Acted as the primary bridge between technical teams and stakeholders to gather requirements, resulting in a streamlined workflow that eliminated operational bottlenecks and significantly enhanced the internal user experience."
          />

          <ProjectCard 
            title="Startup Strategic Planning" 
            tags={['Strategic Roadmap', 'Operations', 'End User Management', 'Resource Planning']}
            description="Foundational project planning and operational readiness for a high-growth business launch."
            deepDive="Facilitated the initial strategic phase of a business launch by managing critical timelines and foundational workflows. Aligned early-stage development with long-term marketing and website requirements to ensure the organization was operationally prepared for scale on day one."
          />

          <ProjectCard 
            title="Centralized Project Management System" 
            tags={['SaaS', 'Change Management', 'Data Centralization', 'Process Automation']}
            description="Standardized PMO implementation to replace fragmented tracking with real-time visibility."
            deepDive="Developed and deployed a centralized project management platform using Smartsheet for a post-acquisition team. Successfully transitioned multiple departments from isolated Excel sheets to a unified system, providing executive leadership with real-time status updates and a single source of truth for all active projects."
          />
        </div>
      </section>

      <ContactForm />

      <footer className="footer mono">
        CyVec&trade; 2026 | ALL SYSTEMS OPERATIONAL
      </footer>
    </div>
  );
};

const ContactForm = () => {
  const [form, setForm] = React.useState({ name: '', email: '', message: '' });
  const [status, setStatus] = React.useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="contact-section">
      <h2 className="section-title">Get In Touch</h2>
      <form className="contact-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="mono">NAME</label>
          <input
            type="text"
            name="name"
            placeholder="Your Name"
            value={form.name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label className="mono">EMAIL</label>
          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={form.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label className="mono">MESSAGE</label>
          <textarea
            name="message"
            rows={5}
            placeholder="Project Inquiries..."
            value={form.message}
            onChange={handleChange}
            required
          />
        </div>
        <button type="submit" className="submit-btn mono" disabled={status === 'sending'}>
          {status === 'sending' ? 'TRANSMITTING...' : 'TRANSMIT'}
        </button>
        {status === 'success' && (
          <p className="form-feedback success mono">MESSAGE RECEIVED. WE WILL BE IN TOUCH.</p>
        )}
        {status === 'error' && (
          <p className="form-feedback error mono">TRANSMISSION FAILED. PLEASE TRY AGAIN.</p>
        )}
      </form>
    </section>
  );
};

const ProjectCard = ({ title, tags, description, deepDive }: { title: string, tags: string[], description: string, deepDive: string }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className={`project-card ${isOpen ? 'active' : ''}`}>
      <div className="project-header" onClick={() => setIsOpen(!isOpen)}>
        <div>
          <h3>{title}</h3>
          <div className="tags">
            {tags.map(tag => <span key={tag} className="tag mono">{tag}</span>)}
          </div>
          <p>{description}</p>
        </div>
        <div className="expand-icon">{isOpen ? '-' : '+'}</div>
      </div>
      {isOpen && (
        <div className="project-detail">
          <h4 className="mono">Technical Deep Dive:</h4>
          <p>{deepDive}</p>
        </div>
      )}
    </div>
  );
};

export default App;
